import {Injectable} from '@angular/core';
import {
  WidgetCellStyle,
  WidgetCellStyleRuleCondition,
  WidgetCellStyleRuleCriteria,
} from '../../models/widget-cell-style-rule';
import {WidgetTableColumn} from '../../models/widget-table-column';

@Injectable({providedIn: 'root'})
export class CellStyleRuleService {
  private readonly hexadecimalColour = /^#[0-9A-Fa-f]{6}$/;

  getCellStyle(value: unknown, column: WidgetTableColumn): WidgetCellStyle | null {
    if (value === null || value === undefined || !this.isRecord(column)) {
      return null;
    }

    const stylingRules = column['stylingRules'];

    if (!Array.isArray(stylingRules)) {
      return null;
    }

    for (const rule of this.getOrderedStylingRules(stylingRules)) {
      if (!this.matches(value, rule)) {
        continue;
      }

      const style = this.sanitiseStyle(this.getRuleStyle(rule));

      if (style) {
        return style;
      }
    }

    return null;
  }

  private matches(value: unknown, rule: unknown): boolean {
    const condition = this.getCondition(rule);

    if (!condition) {
      return false;
    }

    const cellValue = this.normalise(value, condition.caseSensitive);
    const values = condition.values.map((matchingValue) =>
      this.normalise(matchingValue, condition.caseSensitive),
    );

    switch (condition.criteria) {
      case 'EXACT_MATCH':
        return values.some((matchingValue) => cellValue === matchingValue);
      case 'CONTAINS':
        return values.some((matchingValue) => cellValue.includes(matchingValue));
      default:
        return false;
    }
  }

  private getCondition(rule: unknown): WidgetCellStyleRuleCondition | null {
    if (!this.isRecord(rule)) {
      return null;
    }

    const condition = rule['condition'];

    if (!this.isRecord(condition)) {
      return null;
    }

    const criteria = condition['criteria'];

    if (!this.isSupportedCriteria(criteria)) {
      return null;
    }

    const values = condition['values'];

    if (!Array.isArray(values)) {
      return null;
    }

    const stringValues = values.filter(
      (matchingValue): matchingValue is string => typeof matchingValue === 'string',
    );

    if (stringValues.length === 0) {
      return null;
    }

    return {
      criteria,
      values: stringValues,
      caseSensitive:
        typeof condition['caseSensitive'] === 'boolean'
          ? condition['caseSensitive']
          : false,
    };
  }

  private getRuleStyle(rule: unknown): unknown {
    if (!this.isRecord(rule)) {
      return null;
    }

    return rule['style'];
  }

  private getOrderedStylingRules(stylingRules: unknown[]): unknown[] {
    return [...stylingRules].sort(
      (a, b) => this.getOrderIndex(a) - this.getOrderIndex(b),
    );
  }

  private getOrderIndex(rule: unknown): number {
    if (!this.isRecord(rule)) {
      return Number.MAX_SAFE_INTEGER;
    }

    const orderIndex = rule['orderIndex'];

    return typeof orderIndex === 'number'
      ? orderIndex
      : Number.MAX_SAFE_INTEGER;
  }

  private normalise(value: unknown, caseSensitive = false): string {
    const normalisedValue = String(value).trim();

    return caseSensitive ? normalisedValue : normalisedValue.toUpperCase();
  }

  private sanitiseStyle(style: unknown): WidgetCellStyle | null {
    if (!this.isRecord(style)) {
      return null;
    }

    const sanitisedStyle: WidgetCellStyle = {};
    const color = style['color'];

    if (typeof color === 'string' && this.hexadecimalColour.test(color)) {
      sanitisedStyle.color = color;
    }

    const isBold = style['isBold'];

    if (typeof isBold === 'boolean') {
      sanitisedStyle.isBold = isBold;
    }

    const isItalic = style['isItalic'];

    if (typeof isItalic === 'boolean') {
      sanitisedStyle.isItalic = isItalic;
    }

    const isUnderlined = style['isUnderlined'];

    if (typeof isUnderlined === 'boolean') {
      sanitisedStyle.isUnderlined = isUnderlined;
    }

    return Object.keys(sanitisedStyle).length > 0 ? sanitisedStyle : null;
  }

  private isSupportedCriteria(value: unknown): value is WidgetCellStyleRuleCriteria {
    return value === 'EXACT_MATCH' || value === 'CONTAINS';
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
